import { useState, useEffect, useMemo } from 'react';
import { doc, getDoc, setDoc, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useToast } from '../components/ui/Toast';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../hooks/useAuth';
import { logAdminActivity } from '../services/adminActivityLog';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { Input } from '../components/ui/FormField';
import {
  Smartphone,
  Save,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  ShieldAlert,
  Search,
} from 'lucide-react';

/**
 * App Versions — super-admin only.
 *
 * Lifted out of Settings so version management lives in one place:
 *   - Per-app docs at app_settings/{driver_version, customer_version}
 *     hold `latestVersion`, `minVersion`, plus optional update message
 *     copy for the gated update screen.
 *   - Users collection rows already get `platform`, `appVersion`,
 *     `appBuildNumber`, `lastSeen` written on every login (driver app
 *     does it in AuthenticationWrapper, customer app on the same code
 *     path), so we just read those here and bucket by status.
 *
 * Buckets:
 *   updated  — installed appVersion equals latestVersion
 *   outdated — installed > minVersion but < latestVersion (i.e. not on
 *              the newest build, but not blocked either)
 *   blocked  — installed <= minVersion (VersionGate forces an update)
 *   unknown  — no `appVersion` stamped yet (legacy user, never opened
 *              the new build that writes the field)
 */

// Returns -1, 0, 1 like Array.sort. Missing values sort as smallest.
// We tolerate `1.2.3+45` style by stripping the build suffix on `+`.
function compareSemver(a, b) {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  const norm = (v) => v.split('+')[0].split('-')[0].split('.').map((n) => parseInt(n, 10) || 0);
  const [a1, a2, a3] = norm(a);
  const [b1, b2, b3] = norm(b);
  if (a1 !== b1) return a1 < b1 ? -1 : 1;
  if (a2 !== b2) return a2 < b2 ? -1 : 1;
  if (a3 !== b3) return a3 < b3 ? -1 : 1;
  return 0;
}

function bucketFor(installed, latest, min) {
  if (!installed) return 'unknown';
  // "Needs update" only when the installed build is *strictly* below
  // the minimum — being equal to min still counts as a supported build.
  if (min && compareSemver(installed, min) < 0) return 'needs_update';
  if (latest && compareSemver(installed, latest) === 0) return 'updated';
  return 'outdated';
}

const STATUS_META = {
  updated: { label: 'Updated', icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', ring: 'ring-green-200' },
  outdated: { label: 'Outdated', icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', ring: 'ring-amber-200' },
  needs_update: { label: 'Needs update', icon: ShieldAlert, color: 'text-red-600', bg: 'bg-red-50', ring: 'ring-red-200' },
  unknown: { label: 'Unknown', icon: Smartphone, color: 'text-slate-500', bg: 'bg-slate-50', ring: 'ring-slate-200' },
};

const TABS = [
  { key: 'driver', label: 'Driver App', userType: 'driver', settingsDoc: 'driver_version' },
  { key: 'customer', label: 'Customer App', userType: 'customer', settingsDoc: 'customer_version' },
];

export default function AppVersions() {
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const { user: currentAdmin } = useAuth();
  const canManage = hasPermission('manage_app_versions');

  const [activeTab, setActiveTab] = useState('driver');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Per-app version config, keyed by tab.key.
  const [config, setConfig] = useState({
    driver: { latestVersion: '', minVersion: '', updateMessageEn: '', updateMessageAr: '' },
    customer: { latestVersion: '', minVersion: '', updateMessageEn: '', updateMessageAr: '' },
  });

  // Users per app (already filtered by type), fetched once.
  const [users, setUsers] = useState({ driver: [], customer: [] });

  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);

      // Pull both version docs in parallel.
      const [driverSnap, customerSnap] = await Promise.all([
        getDoc(doc(db, 'app_settings', 'driver_version')),
        getDoc(doc(db, 'app_settings', 'customer_version')),
      ]);

      setConfig({
        driver: {
          latestVersion: driverSnap.exists() ? driverSnap.data().latestVersion || '' : '',
          minVersion: driverSnap.exists() ? driverSnap.data().minVersion || '' : '',
          updateMessageEn: driverSnap.exists() ? driverSnap.data().updateMessageEn || '' : '',
          updateMessageAr: driverSnap.exists() ? driverSnap.data().updateMessageAr || '' : '',
        },
        customer: {
          latestVersion: customerSnap.exists() ? customerSnap.data().latestVersion || '' : '',
          minVersion: customerSnap.exists() ? customerSnap.data().minVersion || '' : '',
          updateMessageEn: customerSnap.exists() ? customerSnap.data().updateMessageEn || '' : '',
          updateMessageAr: customerSnap.exists() ? customerSnap.data().updateMessageAr || '' : '',
        },
      });

      // Pull users for both apps in parallel — server-filtered by type
      // so we don't blow up on big customer collections.
      const [driverUsersSnap, customerUsersSnap] = await Promise.all([
        getDocs(query(collection(db, 'users'), where('type', '==', 'driver'))),
        getDocs(query(collection(db, 'users'), where('type', '==', 'customer'))),
      ]);

      // Exclude soft-deleted and half-registered drivers — both pollute
      // the version stats with users that never actually run the app.
      // `incomplete` / `registering` = drivers who verified OTP but never
      // finished onboarding, so they have no `appVersion` stamped.
      const mapDocs = (snap) =>
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(
            (u) =>
              u.status !== 'deleted' &&
              u.isDeleted !== true &&
              u.status !== 'incomplete' &&
              u.status !== 'registering',
          );
      setUsers({
        driver: mapDocs(driverUsersSnap),
        customer: mapDocs(customerUsersSnap),
      });
    } catch (e) {
      console.error('AppVersions load failed:', e);
      toast.error('Failed to load app versions data');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!canManage) return;
    const tab = TABS.find((t) => t.key === activeTab);
    const c = config[activeTab];
    if (!c.minVersion.trim()) {
      toast.error('Minimum version is required');
      return;
    }
    try {
      setSaving(true);
      await setDoc(
        doc(db, 'app_settings', tab.settingsDoc),
        {
          latestVersion: c.latestVersion.trim(),
          minVersion: c.minVersion.trim(),
          updateMessageEn: c.updateMessageEn,
          updateMessageAr: c.updateMessageAr,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      logAdminActivity({
        actor: currentAdmin, action: 'update_app_version',
        targetType: 'settings', targetId: `app_settings/${tab.settingsDoc}`,
        targetLabel: `${tab.label} · latest ${c.latestVersion || '—'} · min ${c.minVersion || '—'}`,
      });
      toast.success(`${tab.label} version saved`);
    } catch (e) {
      console.error('AppVersions save failed:', e);
      toast.error('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const tab = TABS.find((t) => t.key === activeTab);
  const conf = config[activeTab];

  // Derive a row per user with the computed status bucket. Memoized so
  // sorting/filtering doesn't recompute against the raw user list on
  // every keystroke.
  const rows = useMemo(() => {
    const list = users[activeTab] || [];
    return list.map((u) => {
      const status = bucketFor(u.appVersion, conf.latestVersion, conf.minVersion);
      return {
        id: u.id,
        name: u.name || u.fullName || u.firstName || '—',
        phone: u.phoneNumber || u.phone || '—',
        platform: u.platform || '',
        appVersion: u.appVersion || '',
        appBuildNumber: u.appBuildNumber || '',
        accountStatus: u.status || '',
        lastSeen: u.lastSeen,
        status,
      };
    });
  }, [users, activeTab, conf.latestVersion, conf.minVersion]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        r.phone.toLowerCase().includes(q) ||
        r.appVersion.toLowerCase().includes(q)
      );
    });
  }, [rows, statusFilter, search]);

  const counts = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc[r.status] = (acc[r.status] || 0) + 1;
        acc.total += 1;
        return acc;
      },
      { total: 0, updated: 0, outdated: 0, needs_update: 0, unknown: 0 }
    );
  }, [rows]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Smartphone className="w-7 h-7 text-primary-600" />
            App Versions
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage latest & minimum required versions per app, and see which version each user is on.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-semibold transition ${
              activeTab === t.key
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Version config card */}
      <Card>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          {tab.label} — Version Config
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">
              Latest Version
            </label>
            <p className="text-xs text-slate-500 mb-2">
              The newest published build. Used to mark users as "Updated" vs "Outdated".
            </p>
            <Input
              value={conf.latestVersion}
              onChange={(e) =>
                setConfig((prev) => ({
                  ...prev,
                  [activeTab]: { ...prev[activeTab], latestVersion: e.target.value },
                }))
              }
              disabled={!canManage}
              placeholder="1.4.0"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">
              Minimum Required Version *
            </label>
            <p className="text-xs text-slate-500 mb-2">
              Anyone on this version or older is force-blocked with "Update required".
            </p>
            <Input
              value={conf.minVersion}
              onChange={(e) =>
                setConfig((prev) => ({
                  ...prev,
                  [activeTab]: { ...prev[activeTab], minVersion: e.target.value },
                }))
              }
              disabled={!canManage}
              placeholder="1.0.0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Update Message (English)
            </label>
            <textarea
              value={conf.updateMessageEn}
              onChange={(e) =>
                setConfig((prev) => ({
                  ...prev,
                  [activeTab]: { ...prev[activeTab], updateMessageEn: e.target.value },
                }))
              }
              disabled={!canManage}
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-slate-100 text-sm"
              placeholder="Optional. Leave blank to use the default message."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              رسالة التحديث (Arabic)
            </label>
            <textarea
              value={conf.updateMessageAr}
              onChange={(e) =>
                setConfig((prev) => ({
                  ...prev,
                  [activeTab]: { ...prev[activeTab], updateMessageAr: e.target.value },
                }))
              }
              disabled={!canManage}
              rows={3}
              dir="rtl"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-slate-100 text-sm"
              placeholder="اختياري. اتركه فارغًا لاستخدام الرسالة الافتراضية."
            />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={saveConfig} disabled={!canManage || saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Version Config'}
          </Button>
        </div>
      </Card>

      {/* Counts strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <CountTile label="Total" value={counts.total} active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} tone="slate" />
        <CountTile label="Updated" value={counts.updated} active={statusFilter === 'updated'} onClick={() => setStatusFilter('updated')} tone="green" />
        <CountTile label="Outdated" value={counts.outdated} active={statusFilter === 'outdated'} onClick={() => setStatusFilter('outdated')} tone="amber" />
        <CountTile label="Needs update" value={counts.needs_update} active={statusFilter === 'needs_update'} onClick={() => setStatusFilter('needs_update')} tone="red" />
        <CountTile label="Unknown" value={counts.unknown} active={statusFilter === 'unknown'} onClick={() => setStatusFilter('unknown')} tone="slate" />
      </div>

      {/* Search */}
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, phone, or version…"
              className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
          >
            <option value="all">All statuses</option>
            <option value="updated">Updated</option>
            <option value="outdated">Outdated</option>
            <option value="needs_update">Needs update</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>

        {/* Users table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold">User</th>
                <th className="px-3 py-2 font-semibold">Phone</th>
                <th className="px-3 py-2 font-semibold">Platform</th>
                <th className="px-3 py-2 font-semibold">Installed</th>
                <th className="px-3 py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-10 text-center text-slate-400">
                    No users match this filter.
                  </td>
                </tr>
              ) : (
                filteredRows.map((r) => (
                  <UserRow key={r.id} row={r} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function CountTile({ label, value, active, onClick, tone }) {
  const tones = {
    slate: { ring: 'ring-slate-200', text: 'text-slate-900', activeBg: 'bg-slate-100' },
    green: { ring: 'ring-green-200', text: 'text-green-700', activeBg: 'bg-green-50' },
    amber: { ring: 'ring-amber-200', text: 'text-amber-700', activeBg: 'bg-amber-50' },
    red: { ring: 'ring-red-200', text: 'text-red-700', activeBg: 'bg-red-50' },
  };
  const t = tones[tone] || tones.slate;
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-xl px-4 py-3 ring-1 transition ${t.ring} ${
        active ? t.activeBg : 'bg-white hover:bg-slate-50'
      }`}
    >
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-bold ${t.text}`}>{value}</div>
    </button>
  );
}

function UserRow({ row }) {
  const meta = STATUS_META[row.status];
  const Icon = meta.icon;
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50">
      <td className="px-3 py-2 font-medium text-slate-900">{row.name}</td>
      <td className="px-3 py-2 text-slate-600">{row.phone}</td>
      <td className="px-3 py-2">
        <PlatformBadge platform={row.platform} />
      </td>
      <td className="px-3 py-2 text-slate-700">
        {row.appVersion ? (
          <span className="font-mono">{row.appVersion}</span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        <span
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ring-1 ${meta.bg} ${meta.color} ${meta.ring}`}
          title={meta.label}
        >
          <Icon className="w-3.5 h-3.5" />
          {meta.label}
        </span>
      </td>
    </tr>
  );
}

// Same SVGs used on the Map page so the visual language stays consistent.
const AndroidIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="#3DDC84">
    <path d="M17.523 15.341c-.5 0-.906-.406-.906-.906s.406-.906.906-.906.906.406.906.906-.406.906-.906.906zm-11.046 0c-.5 0-.906-.406-.906-.906s.406-.906.906-.906.906.406.906.906-.406.906-.906.906zm11.4-6.839l1.932-3.346c.108-.187.044-.427-.143-.535-.187-.108-.427-.044-.535.143l-1.958 3.39c-1.48-.674-3.14-1.054-4.93-1.054s-3.45.38-4.93 1.054l-1.958-3.39c-.108-.187-.348-.251-.535-.143-.187.108-.251.348-.143.535l1.932 3.346c-3.165 1.715-5.285 4.89-5.285 8.498h21.75c0-3.608-2.12-6.783-5.285-8.498z" />
  </svg>
);

const AppleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="#555555">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

function PlatformBadge({ platform }) {
  if (!platform) return <span className="text-slate-400">—</span>;
  if (platform === 'android') return <AndroidIcon />;
  if (platform === 'ios') return <AppleIcon />;
  return <span className="text-slate-400">—</span>;
}
